import React, { useContext, useEffect, useState } from 'react';
import { useTicketsStore } from '../store/ticketsStore';
import { InsidePageContainer, PageContainer } from '../common/style';
import { useHistory } from 'react-router-dom';
import Header from '../common/Header';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { TicketsCart, getTotalProductNum } from '../common';
import AssignContainer from './sections/AssignContainer';
import { useForm } from 'react-hook-form';
import { useNetwork } from 'services/network';
import { LoaderCountContext } from 'context/LoaderV5';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { Roles } from 'utils/constants';
import {
  dataLayerPagesEnum,
  setVirtualPageDataLayer,
} from 'utils/dataLayerManager';

export const commonDefaultValues = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  reservations: [],
  owner_flag_privacy_1: '3',
  owner_flag_privacy_2: '3',
  owner_flag_privacy_3: '3',
};

export const operatorDefaultValues = {
  ...commonDefaultValues,
};

// const otherDefaultValues = {
//   privacy: undefined,
//   notify: undefined,
//   country: undefined,
//   region: undefined,
//   district: undefined,
//   city: undefined,
//   sdm_role_in_company: undefined,
//   sdm_operator_type: undefined,
//   sdm_operator_sub_type: undefined,
//   sdm_other_role_in_company: undefined,
//   sdm_other_type: undefined,
// };

const AssignTicket = ({ locale, loggedUser }) => {
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    productsInCart,
    stepperCurrentTicketIdx,
    ticketsData,
    isEditingTicketMode,
    loggedDataAtIndex,
  } = useTicketsStore();
  const history = useHistory();
  const [currentTicket, setCurrentTicket] = useState();
  const [sameEmailAlreadyChecked, setSameEmailAlreadyChecked] = useState(false);
  const [validEmailAlreadyChecked, setValidEmailAlreadyChecked] =
    useState(false);
  const { setLoaderActive } = useContext(LoaderCountContext);
  const network = useNetwork();
  const intl = useIntl();
  const i18nAssign = translations.TicketsV2(intl).Assign;
  const i18nCommon = translations.TicketsV2(intl).Common;
  const i18nErrors = i18nAssign.errors;
  const i18nLegacy = translations.Tickets(intl).edit.ticketEditCard;
  const isOperator = loggedUser.type === Roles.OPERATOR;

  const closeAlert = (sev) =>
    useTicketsStore.setState({ alertData: { severity: sev } });

  const form = useForm({
    commonDefaultValues,
    shouldUnregister: false,
  });

  const { handleSubmit, setError } = form;

  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  // Effects
  useEffect(() => {
    const productsWithoutQuantity = productsInCart
      .map((pro) => {
        // if (pro.quantity === 1) return pro;
        // if (pro.quantity === 0) return undefined;
        return Array.from({ length: pro.quantity }, (_, i) => i).map(() => pro);
      })
      .flat();
    setCurrentTicket(
      ticketsData?.length
        ? ticketsData[stepperCurrentTicketIdx]?.ticketData
        : productsWithoutQuantity[stepperCurrentTicketIdx]
    );
    if (!ticketsData.length)
      useTicketsStore.setState({
        ticketsData: productsWithoutQuantity.map((prod) => ({
          assignData: null,
          ticketData: prod,
        })),
      });
  }, [stepperCurrentTicketIdx, ticketsData.length]);

  useEffect(() => {
    if (!ticketsData?.length) return;

    let cleanTicketsData = ticketsData.filter((t) => t.assignData);

    productsInCart?.forEach((p) => {
      const typeQtyToAdd =
        p.quantity -
        cleanTicketsData.filter((t) => t.ticketData.id === p.id).length;

      const productsToAdd = Array(typeQtyToAdd).fill({
        assignData: null,
        ticketData: p,
      });

      cleanTicketsData = [...cleanTicketsData, ...productsToAdd];
    });

    useTicketsStore.setState({
      ticketsData: cleanTicketsData,
    });
  }, []);

  useEffect(() => {
    setVirtualPageDataLayer(dataLayerPagesEnum.TICKETS_ASSIGN);
  }, []);

  // Functions
  const validatePhone = (phone) => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone || trimmedPhone?.length < 7) {
      throw `${phone.includes('+') ? '' : '+'}${phone} ${
        i18nLegacy.invalidPhone
      }`;
    }
    // const phoneRegez = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
    // const found = trimmedPhone.match(phoneRegez);
    return trimmedPhone;
  };
  const handleTicketsStepper = async (data) => {
    // After native fields validation manually check...
    // for valid phone number
    let validatedPhone;
    try {
      validatedPhone = validatePhone(data.phone);
    } catch (err) {
      setError('phone', {
        type: 'custom',
        message: err,
      });
    }
    if (!validatedPhone) return;
    // ... for selected dates ...
    const selectedReservations = data.reservations.filter((s) => s.selected);
    const hasSelectedDates = selectedReservations.length;

    const enoughDate = data.reservations.length >= currentTicket.capacity;
    const datesToSelectCount = enoughDate
      ? currentTicket.capacity
      : data.reservations.length;
    if (
      !isOperator &&
      ((enoughDate && selectedReservations.length < currentTicket.capacity) ||
        (!enoughDate && selectedReservations.length < data.reservations.length))
    ) {
      const isSingleDay = currentTicket.capacity === 1;

      const title = isSingleDay
        ? `${i18nErrors.missingDates.warning} ${i18nErrors.missingDates.warningOneDay}`
        : `${i18nErrors.missingDates.warning} ${
            hasSelectedDates ? '' : `${i18nErrors.missingDates.all}`
          } ${i18nErrors.missingDates.the} ${datesToSelectCount} ${
            i18nErrors.missingDates.dates
          }`;
      return useTicketsStore.setState({
        alertData: {
          title,
          message: (
            <span>
              {i18nErrors.missingDates.before}&nbsp;
              <a href="#dates-selection">
                {
                  i18nErrors.missingDates[
                    isSingleDay ? 'selectDateOneDay' : 'selectDates'
                  ]
                }{' '}
              </a>
            </span>
          ),
          onClose: () => closeAlert('error'),
          severity: 'error',
        },
      });
    }
    if (isOperator && selectedReservations.length < 1) {
      const title = i18nErrors.operatorOneDate.title;
      return useTicketsStore.setState({
        alertData: {
          title,
          message: (
            <span>
              {i18nErrors.operatorOneDate.subtitleOne}{' '}
              <a href="#dates-selection">
                {' '}
                {i18nErrors.operatorOneDate.subtitleLink}{' '}
              </a>
              {i18nErrors.operatorOneDate.subtitleTwo}
            </span>
          ),
          onClose: () => closeAlert('error'),
          severity: 'error',
        },
      });
    }

    // ...privacy check ...
    const wPrivacy = data.owner_flag_privacy_1;
    const wPrivacy2 = data.owner_flag_privacy_2;
    const wPrivacy3 = data.owner_flag_privacy_3;
    if (!wPrivacy2 || wPrivacy2 === '3') {
      setError('owner_flag_privacy_2', {
        type: 'custom',
        message: i18nAssign.flags.errors.data,
      });
    }
    if (!wPrivacy3 || wPrivacy3 === '3') {
      setError('owner_flag_privacy_3', {
        type: 'custom',
        message: i18nAssign.flags.errors.data,
      });
    }
    if (!wPrivacy || wPrivacy !== '1') {
      const title = i18nErrors.privacy.title;
      setError('owner_flag_privacy_1', {
        type: 'custom',
        message: i18nAssign.flags.errors.privacy,
      });
      return useTicketsStore.setState({
        alertData: {
          title,
          message: (
            <span>
              {i18nErrors.missingDates.before}&nbsp;
              <a href="#dates-selection">{i18nErrors.privacy.subtitle}</a>
            </span>
          ),
          onClose: () => closeAlert('error'),
          severity: 'error',
        },
      });
    }
    if (!wPrivacy2 || !wPrivacy3 || wPrivacy2 === '3' || wPrivacy3 === '3') {
      const title = i18nCommon.caution;
      return useTicketsStore.setState({
        alertData: {
          title,
          message: (
            <span>
              {i18nErrors.generalPrivacy.descriptionOne}{' '}
              <a href="#dates-selection">
                {i18nErrors.generalPrivacy.descriptionLink}{' '}
              </a>
              {i18nErrors.generalPrivacy.descirptionTwo}
            </span>
          ),
          onClose: () => closeAlert('error'),
          severity: 'error',
        },
      });
    }

    // ... email validity ...
    const existingEmailIdx = ticketsData.findIndex(
      (t) => t.assignData?.email === data.email
    );
    if (
      existingEmailIdx >= 0 &&
      existingEmailIdx !== stepperCurrentTicketIdx &&
      !sameEmailAlreadyChecked
    ) {
      setError('email', {
        type: 'custom',
        message: i18nErrors.emailAlreadyUsed.title,
      });
      setSameEmailAlreadyChecked(true);
      return useTicketsStore.setState({
        alertData: {
          title: i18nErrors.emailAlreadyUsed.title,
          message: i18nErrors.emailAlreadyUsed.message,
          onClose: () => closeAlert('warning'),
          severity: 'warning',
        },
      });
    }

    if (
      loggedDataAtIndex !== stepperCurrentTicketIdx &&
      !validEmailAlreadyChecked
    ) {
      showLoader();
      const validateRes = await network.ticketingLambda({
        params: `/gcf/validate_email`,
        customMethod: 'post',
        data: {
          params: `/gcf/validate_email`,
          customMethod: 'post',
          data: { email: data.email },
        },
        federationId: loggedUser?.salId,
      });
      hideLoader();
      setValidEmailAlreadyChecked(true);
      if (!validateRes.valid) {
        setError('email', {
          type: 'custom',
          message: i18nErrors.emailNotValid.title,
        });
        return useTicketsStore.setState({
          alertData: {
            title: i18nErrors.emailNotValid.title,
            message: (
              <span>
                {i18nErrors.emailNotValid.subtitle}&nbsp;
                <a href="#data-form">{i18nErrors.emailNotValid.email}</a>
              </span>
            ),
            onClose: () => closeAlert('warning'),
            severity: 'warning',
          },
        });
      }
    }

    const isStudent = currentTicket?.code?.includes('_STUD_');

    const nextTicketData = {
      assignData: {
        ...data,
        ...((!isOperator || isStudent) && {
          sdm_operator_type: 'prv',
        }),
        ...(isStudent && { sdm_operator_sub_type: 'prv_std' }),
      },
      ticketData: currentTicket,
    };
    const nextTicketsData = [...useTicketsStore.getState().ticketsData];
    nextTicketsData[stepperCurrentTicketIdx] = nextTicketData;
    useTicketsStore.setState({
      ticketsData: nextTicketsData,
    });

    // Increase assign stepper or go to recap
    if (
      stepperCurrentTicketIdx + 2 > getTotalProductNum(productsInCart) ||
      isEditingTicketMode
    ) {
      useTicketsStore.setState({
        isEditingTicketMode: false,
      });
      history.push('/recap');
    } else {
      setSameEmailAlreadyChecked(false);
      setValidEmailAlreadyChecked(false);
      useTicketsStore.setState((prev) => ({
        stepperCurrentTicketIdx: prev.stepperCurrentTicketIdx + 1,
      }));
    }
  };

  const handleBack = () =>
    stepperCurrentTicketIdx > 0
      ? useTicketsStore.setState((prev) => ({
          stepperCurrentTicketIdx: prev.stepperCurrentTicketIdx - 1,
        }))
      : history.push('/merch');

  const btnText = isEditingTicketMode
    ? i18nCommon.backToRecap
    : stepperCurrentTicketIdx + 2 > getTotalProductNum(productsInCart)
    ? i18nCommon.proceedMsg
    : `${i18nCommon.assignTicket} ${stepperCurrentTicketIdx + 1} ${
        i18nCommon.ofText
      } ${getTotalProductNum(productsInCart)}`;

  const isCartOpen =
    productsInCart?.length &&
    productsInCart?.some((pro) => pro.quantity && pro.quantity > 0);
  const isApp = isCartOpen && window.location.pathname.includes('app');

  return (
    <PageContainer direction={'column'} forcePaddingBottom={isApp}>
      <InsidePageContainer>
        <Header backFn={handleBack} loggedUser={loggedUser} />
        <Stack direction={isLgDown ? 'column' : 'row'}>
          {productsInCart && productsInCart?.length && currentTicket ? (
            <AssignContainer
              locale={locale}
              loggedUser={loggedUser}
              form={form}
              currentTicket={currentTicket}
              setCurrentTicket={setCurrentTicket}
              assignTicketTexts={i18nAssign}
            />
          ) : (
            <Stack
              style={{
                width: isLgDown ? '100%' : '64%',
                ...(!isLgDown && {
                  paddingRight: '70px',
                }),
              }}
              gap={'25px'}
            ></Stack>
          )}
          {isCartOpen ? (
            <TicketsCart
              locale={locale}
              procideFn={handleSubmit(handleTicketsStepper)}
              procideMsg={btnText}
            />
          ) : null}
        </Stack>
      </InsidePageContainer>
    </PageContainer>
  );
};
export default AssignTicket;
